import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";

interface DiscountInputProps {
    id?: string;
    value?: number;
    onChange: (value: number | undefined) => void;
    placeholder?: string;
    readOnly?: boolean;
    error?: string;
    className?: string;
    decimalPlaces?: number;
}

export function DiscountInput({
    id,
    value,
    onChange,
    placeholder = "0",
    readOnly,
    error,
    className,
    decimalPlaces,
}: DiscountInputProps) {
    const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        setInputValue(value?.toString() ?? "");
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleBlur = () => {
        if (inputValue === "") {
            onChange(undefined);
        } else {
            const parsed = parseFloat(inputValue);
            const rounded =
                decimalPlaces !== undefined
                    ? Math.round(parsed * Math.pow(10, decimalPlaces)) /
                      Math.pow(10, decimalPlaces)
                    : parsed;

            if (decimalPlaces !== undefined) {
                setInputValue(rounded.toFixed(decimalPlaces));
            }

            onChange(rounded);
        }
    };

    const showPercentage = value !== undefined && value > 0;
    const inputClassName = `${error ? "border-red-500" : ""} ${readOnly ? "bg-gray-50" : ""} ${showPercentage ? "pr-8" : ""} ${className || ""}`;

    return (
        <div className="w-full">
            <div className="relative">
                <Input
                    id={id}
                    type="number"
                    value={inputValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    readOnly={readOnly}
                    className={inputClassName}
                    placeholder={placeholder}
                />
                {showPercentage && (
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
                        %
                    </span>
                )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
                Enter a positive value for percentage discount (e.g., 10 for
                10%) or a negative value for fixed amount (e.g., -5 for $5 off)
            </div>
            {error && (
                <p className="text-xs text-red-600 mt-1 break-words w-full">
                    {error}
                </p>
            )}
        </div>
    );
}
